import type { ReactNode } from 'react';
import { Caption1, Text, makeStyles } from '@fluentui/react-components';
import { tokens } from '@vigeo/ui';

const useStyles = makeStyles({
  shell: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100dvh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalL,
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  main: {
    flexGrow: 1,
    width: '100%',
    maxWidth: '40rem',
    marginInline: 'auto',
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  footer: {
    paddingInline: tokens.spacingHorizontalL,
    paddingBlock: tokens.spacingVerticalL,
    textAlign: 'center',
  },
});

export interface PortalShellProps {
  readonly children: ReactNode;
}

/**
 * The candidate's whole world (ADR-004).
 *
 * There is deliberately no navigation, no person switcher, no route into `/app`, and no
 * sign-out — because there is nothing to sign out of. Field staff have no account, and an
 * affordance that looks like one would send them hunting for a password that does not
 * exist.
 *
 * `100dvh` rather than `100vh`: mobile Safari's toolbar makes `vh` taller than the visible
 * area, which pushes a submit button under the browser chrome on exactly the devices this
 * is built for.
 */
export function PortalShell({ children }: PortalShellProps) {
  const styles = useStyles();
  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <Text weight="semibold" size={400}>
          Vigeo Home Health
        </Text>
      </header>
      <main className={styles.main}>{children}</main>
      <footer className={styles.footer}>
        <Caption1>If something here does not look right, contact our office.</Caption1>
      </footer>
    </div>
  );
}
